// src/components/CameraConfig.js
import React, { useState, useEffect } from "react"; // Import useEffect
import { Form, Row, Col } from "react-bootstrap";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal";
import DropdownComponent from "../../../components/UI_Kit/Dropdown/Dropdown";
import "../styles.css";
import api from "../../../services/api"; // Assuming your api service is here
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CameraConfig = ({ onSave, initialCameras = [], deleteCamera }) => {
  const [showModal, setShowModal] = useState(false);
  const [cameraToDelete, setCameraToDelete] = useState(null);
  const [cameraRoles, setCameraRoles] = useState([]); // State to store camera roles

  const handleDeleteCamera = (cameraId) => {
    setCameraToDelete(cameraId);
    setShowModal(true);
  };

  const confirmDeleteCamera = () => {
    // Find the camera to check if it's a new one
    const camera = cameras.find(cam => cam.id === cameraToDelete);

    // If the camera doesn't have an API ID (it's new and not saved yet)
    // Just remove it from the local state without calling the API
    if (camera && !initialCameras.some(c => c.id === camera.id)) {
      // This is a new camera, just remove it from state
      setCameras(cameras.filter(cam => cam.id !== cameraToDelete));
    } else {
      // This is an existing camera, call the API to delete it
      deleteCamera(cameraToDelete);
    }

    setShowModal(false);
    setCameraToDelete(null);
  };

  // Map initialCameras to use 'viewingArea' internally
  const mapToInternalFormat = (camera) => ({
    ...camera,
    viewingArea: camera.vision_area || camera.viewingArea || "", // Map vision_area to viewingArea
  });

  // Map internal format back to API format ('vision_area')
  const mapToApiFormat = (camera) => {
    const { viewingArea, ...rest } = camera; // Destructure to separate viewingArea
    return {
      ...rest,
      vision_area: viewingArea, // Map viewingArea back to vision_area
    };
  };

  const [cameras, setCameras] = useState(
    initialCameras.length > 0
      ? initialCameras.map(mapToInternalFormat) // Map initial data
      : [{ id: Date.now(), role: "", name: "", viewingArea: "" }] // Use timestamp for potential default ID
  );

  // Add useEffect to update state if initialCameras prop changes after mount
  useEffect(() => {
    setCameras(
      initialCameras.length > 0
        ? initialCameras.map(mapToInternalFormat)
        : [{ id: Date.now(), role: "", name: "", viewingArea: "" }]
    );
  }, [initialCameras]); // Dependency array ensures this runs when initialCameras changes

  // useEffect to fetch camera roles
  useEffect(() => {
    const fetchCameraRoles = async () => {
      try {
        const response = await api.get("/prices"); //  API service instance

        // Find the item with type "Constitution du point"
        const constitutionPointItem = response.data.find(item => item.type === "Constitution du point");

        if (constitutionPointItem && constitutionPointItem.champ && constitutionPointItem.champ[0] && constitutionPointItem.champ[0].choix) {
          setCameraRoles(constitutionPointItem.champ[0].choix);
        } else {
          console.warn("Could not find 'Constitution du point' or expected 'choix' array in API response.");
          setCameraRoles([]); // Set to empty array if not found or structure is wrong
        }
      } catch (error) {
        console.error("Error fetching camera roles:", error);
        setCameraRoles([]);
      }
    };

    fetchCameraRoles();
  }, []); // Empty dependency array means this runs once on mount

  const handleSave = () => {
    // Validate all camera fields before saving
    for (const camera of cameras) {
      if (!camera.role) {
        toast.error(`Veuillez sélectionner un rôle pour la caméra ${camera.id}.`);
        return;
      }
      if (!camera.name.trim()) {
        toast.error(`Veuillez entrer un nom pour la caméra ${camera.id}.`);
        return;
      }
      if (!camera.viewingArea.trim()) {
        toast.error(`Veuillez décrire la zone à visionner pour la caméra ${camera.id}.`);
        return;
      }
    }
    // Map the internal state back to the API format before saving
    const camerasToSave = cameras.map(mapToApiFormat);
    onSave(camerasToSave);
  };

  const handleCameraChange = (cameraId, field, value) => {
    setCameras((prev) =>
      prev.map((cam) =>
        cam.id === cameraId ? { ...cam, [field]: value } : cam
      )
    );
  };

  const addCamera = () => {
    if (cameras.length < 30) {
      // Use a more robust way to generate unique IDs, e.g., timestamp or a library
      const newId = Date.now(); // Simple example using timestamp
      setCameras([
        ...cameras,
        { id: newId, role: "", name: "", viewingArea: "" },
      ]);
    }
  };

  return (
    <>
      <ToastContainer />
      {cameras.map((camera) => (
        <div key={camera.id} className="camera-section mb-4 p-3  rounded">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Caméra {camera.id}</h6>
            {cameras.length > 1 && (
              <img
                onClick={() => handleDeleteCamera(camera.id)}
                src="/Cross.png"
                alt="Delete camera"
                className="delete-camera-button"
              ></img>
            )}
          </div>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId={`cameraRole${camera.id}`}>
                <Form.Label>Rôle de la caméra *</Form.Label>
                <DropdownComponent
                  name="role"
                  value={camera.role}
                  onChange={(e) =>
                    handleCameraChange(camera.id, "role", e.target.value)
                  }
                  options={cameraRoles.map(roleOption => ({ label: roleOption.titre, value: roleOption.titre }))}
                  placeholder="Sélectionner..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId={`cameraName${camera.id}`}>
                <Form.Label>Nom de la caméra *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Nommer la caméra"
                  value={camera.name}
                  onChange={(e) =>
                    handleCameraChange(camera.id, "name", e.target.value)
                  }
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row for Viewing Area */}
          <Row className="mb-3">
            <Col>
              <Form.Group controlId={`viewingArea${camera.id}`}>
                <Form.Label>Zone à visionner *</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Ajouter un commentaire..."
                    name="viewingArea"
                    value={camera.viewingArea || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 300) {
                        // Update uses the internal 'viewingArea'
                        handleCameraChange(camera.id, "viewingArea", value);
                      }
                    }}
                    required
                    maxLength={300}
                    className="comment-box"
                  />
                  <div
                    className="text-end text-muted"
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      right: 7,
                      background: 'rgba(255,255,255,0.8)',
                      padding: '0 6px',
                      fontSize: '16px',
                      color: '#888 !important',
                      pointerEvents: 'none'
                    }}
                  >
                    <small>
                      {(camera.viewingArea || "").length}/300
                    </small>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </div>
      ))}

      {cameras.length < 30 && (
        <div className="add-camera-button mb-4">
          <span variant="success" onClick={addCamera}>
            <img src="/plus.png" alt="Add camera"></img> Ajouter une caméra
          </span>
        </div>
      )}

      <div className="footer shadow-lg d-flex justify-content-center">
        <button
          className="w-100 primary-button primary-button-block"
          // Changed type to "button" to prevent default form submission if wrapped in <Form>
          type="button"
          onClick={handleSave} // handleSave now maps data before calling onSave
        >
          Sauvegarder Caméras {/* Changed button text for clarity */}
        </button>
      </div>

      <ConfirmationModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleConfirm={confirmDeleteCamera}
        title="Confirmer la suppression"
        buttonTitle="Supprimer"
        LinkTitle="Annuler"
      >
        Êtes-vous sûr de vouloir supprimer cette caméra ?
      </ConfirmationModal>
    </>
  );
};

export default CameraConfig;
